import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Dialog, DialogContent, withStyles } from '@material-ui/core';
import flowRight from 'lodash.flowright';
import { connect } from 'react-redux';

import CustomDialogTitle from '../../partials/customDialogTitle/CustomDialogTitle';
import DraggableDialog from '../../partials/DraggableDialog';
import styles from './styles';
import { actions as reduxActions } from './reducers';
import { translate } from '../../i18n/I18nProvider';
import ActionsButtons from './components/ActionsButtons';
import { actions as numberRangereduxActions } from '../../pages/externalNumberRange/reducers';
import DataBox from '../../partials/dataBox/DataBox';
import Optional from '../../utils/optional';
import StatusCell from '../../pages/externalNumberRange/components/StatusCell';
import calculateSplitInfo from './actions/calculateSplitInfo';
import splitRange from './actions/splitRange';
import { getExternalNoRanges } from '../../pages/externalNumberRange/actions/getExternalNoRanges';

const SplitNumberRange = ({
  classes,
  open,
  closeSplitNumberRange,
  ranges,
  refreshRangeTable,
  resetValues,
  isBeTable,
  getExternalNoRanges
}) => {
  const rangeToShow = useMemo(() => {
    if (ranges.length > 0) {
      return ranges[0];
    }
    return {};
  }, [ranges]);

  const splitInfo = useMemo(
    () => calculateSplitInfo(rangeToShow?.interval || 0),
    [rangeToShow?.interval]
  );

  const handleSubmit = useCallback(async () => {
    const result = await splitRange({ rangeId: rangeToShow.id });

    if (result === true) {
      if (isBeTable) {
        refreshRangeTable(true);
      } else {
        getExternalNoRanges({ ...resetValues });
      }

      closeSplitNumberRange();
    }
  }, [
    rangeToShow.id,
    refreshRangeTable,
    closeSplitNumberRange,
    isBeTable,
    resetValues,
    getExternalNoRanges
  ]);

  return (
    <Dialog
      maxWidth="md"
      classes={{
        paper: classes.dialogContent
      }}
      PaperComponent={DraggableDialog}
      open={open}
      onClose={(event, reason) => {
        if (reason !== 'backdropClick') {
          closeSplitNumberRange();
        }
      }}
    >
      <CustomDialogTitle onCloseClick={closeSplitNumberRange}>
        {translate('NUMBER_RANGES.SPLIT_NUMBER_RANGE')}
      </CustomDialogTitle>
      <DialogContent className={classes.dialogContent}>
        <div className={classes.dataRow}>
          <DataBox
            label={translate('TABLE.RANGE_FROM')}
            value={rangeToShow?.range_from}
            width={120}
          />
          <DataBox
            label={translate('TABLE.RANGE_TO')}
            value={rangeToShow?.range_up_to_incl}
            width={120}
          />
          <DataBox
            label={translate('STATUS')}
            value={<StatusCell status={rangeToShow?.status} />}
            width={120}
          />
        </div>
        <div className={classes.dataRow}>
          <DataBox
            label={translate('TABLE.NETWORK')}
            value={Optional(rangeToShow?.provider?.sane_name).or(
              Optional(rangeToShow?.provider?.name).or('')
            )}
            width={180}
          />

          <DataBox
            label={translate('TABLE.TYPE')}
            value={rangeToShow?.number_type}
            width={100}
          />
          <DataBox
            label={translate('TABLE.NO_OF_NUMBERS')}
            value={rangeToShow?.interval}
            width={50}
          />
        </div>
        {splitInfo && (
          <div className={classes.splitInfo}>
            <div className={classes.splitInfoLabel}>
              {translate('NUMBER_RANGES.SPLIT_DESCRIPTION')}:
            </div>
            <div className={classes.splitInfoText}>
              {getSplitText(splitInfo, translate)}
            </div>
          </div>
        )}
      </DialogContent>
      <div className={classes.footer}>
        <ActionsButtons
          onCloseClick={closeSplitNumberRange}
          handleSubmit={handleSubmit}
        />
      </div>
    </Dialog>
  );
};

const mapStatesToProps = ({ splitNumberRange, selectClient }) => ({
  ...splitNumberRange,
  selectOptions: selectClient.selectClientsList
});

const mapDispatchToProps = {
  closeSplitNumberRange: reduxActions.closeSplitRange,
  refreshRangeTable: numberRangereduxActions.setRefresh,
  getExternalNoRanges
};

export default flowRight(
  connect(mapStatesToProps, mapDispatchToProps),
  withStyles(styles)
)(SplitNumberRange);

const getSplitText = (splitInfo, translateFn) => {
  if (!splitInfo) return '';

  const parts = splitInfo.map(split => {
    return translateFn('NUMBER_RANGES.SPLIT_RANGES', {
      count: split.count,
      size: split.size
    });
  });

  return parts.join(` ${translateFn('AND')} `);
};
